import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, ArrowRight, Loader2 } from "lucide-react";
import { API_URL } from "../utils/api";

// Header, footer and widget are provided by <NexusLayout /> — do NOT duplicate
// them here, that's what was producing the stale double-header on /blog.

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog`);
        if (response.ok) {
          const data = await response.json();
          setPosts(data.posts || []);
        }
      } catch (e) {
        console.error("Failed to load posts:", e);
      } finally {
        setLoading(false);
      }
    };
    loadPosts();
  }, []);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("sv-SE", {
      year: "numeric", month: "long", day: "numeric",
    });

  return (
    <>
      <Helmet>
        <title>Blogg | Elido - Tips och nyheter om hemsidor</title>
        <meta name="description" content="Läs våra artiklar om hemsidor, webbdesign och digitala lösningar för företag." />
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-[#0F2A30] pt-16 pb-16 lg:pt-20 lg:pb-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Blogg</h1>
            <p className="text-xl text-gray-300">
              Tips, guider och nyheter om hemsidor och digital närvaro.
            </p>
          </div>
        </section>

        {/* Posts */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#255E69]" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <h2 className="text-xl font-semibold text-gray-700 mb-2">Inga inlägg ännu</h2>
                <p className="text-gray-500">Kom tillbaka snart för nya artiklar!</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <article
                    key={post.id}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-shadow"
                  >
                    {post.featured_image && (
                      <Link to={`/blog/${post.slug}`}>
                        <img
                          src={post.featured_image}
                          alt={post.featured_image_alt || post.title}
                          className="w-full h-48 object-cover"
                        />
                      </Link>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.created_at)}
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <h2 className="text-xl font-bold text-gray-900 mb-2 hover:text-[#255E69] transition-colors">
                          {post.title}
                        </h2>
                      </Link>
                      <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center gap-2 text-[#255E69] font-medium hover:gap-3 transition-all"
                      >
                        Läs mer
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-[#255E69] to-[#3A8A98]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Behöver du hjälp med din hemsida?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Kontakta oss för en kostnadsfri konsultation.
            </p>
            <Link
              to="/#kontakt"
              className="inline-flex items-center gap-2 bg-white text-[#255E69] px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Kontakta oss
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
